import {useState, useEffect, useCallback} from 'react';
import yjsDocumentService from '../../infrastructure/sync/YjsDocumentService';

const useYjsDocument = (workspaceId, filePath, {contentOnly = false} = {}) => {
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workspaceId) {
      setError('Workspace ID is required');
      return () => {};
    }

    // File path is required for document content operations
    const needsFilePath = filePath !== undefined && filePath !== null;
    
    try {
      // Connect to document with or without file path
      const result = yjsDocumentService.connectToDocument(workspaceId, filePath);
      const { doc, provider } = result;
      
      // Set initial content only if file path is provided
      if (needsFilePath && result.text) {
        setContent(result.text.toString());
      }
      
      // Set initial connection state
      if (!contentOnly) {setIsConnected(provider.wsconnected); setIsSynced(provider.synced);}

      // Subscribe to changes only if file path is provided
      let unsubText = () => {};
      let unsubMeta = () => {};
      
      if (needsFilePath) {
        unsubText = yjsDocumentService.subscribeToTextChanges(
          workspaceId, filePath, newText => setContent(newText)
        );
        if (!contentOnly) unsubMeta = yjsDocumentService.subscribeToMetadataChanges(
          workspaceId, filePath, newMeta => setMetadata(newMeta)
        );
      }

      // Define explicit event handlers with proper logging
      const statusHandler = e => {
        console.log(`Connection status changed: ${e.status}`);
        setIsConnected(e.status === 'connected');
      };
      
      const syncHandler = syncState => {
        console.log(`Sync state changed: ${syncState ? 'synced' : 'syncing'}`);
        setIsSynced(syncState);
      };
      
      const awarenessHandler = () => {
        const users = yjsDocumentService.getAwarenessInfo(workspaceId);
        console.log(`Awareness changed: ${users.length} users`);
        setConnectedUsers(users);
      };

      // Register event handlers
      if (!contentOnly) {provider.on('status', statusHandler); provider.on('sync', syncHandler);}

      if (!contentOnly && provider.awareness) {
        provider.awareness.on('change', awarenessHandler);
        awarenessHandler();
      }

      return () => {
        if (!contentOnly) {provider.off('status', statusHandler); provider.off('sync', syncHandler);}
        if (!contentOnly && provider.awareness) provider.awareness.off('change', awarenessHandler);
        if (needsFilePath) {
          unsubText();
          unsubMeta();
        }
      };
    } catch (err) {
      setError(err.message);
      return () => {};
    }
  }, [workspaceId, filePath, contentOnly]);

  const updateContent = useCallback(newContent => {
    if (!workspaceId) {
      setError('Workspace ID is required');
      return;
    }

    if (!filePath) {
      setError('File path is required for content updates');
      return;
    }

    try {
      yjsDocumentService.updateDocument(workspaceId, newContent, filePath);
    } catch (err) {
      setError(err.message);
    }
  }, [workspaceId, filePath]);

  const updateMetadata = useCallback(metaUpdate => {
    if (!workspaceId) {
      setError('Workspace ID is required');
      return;
    }

    if (!filePath) {
      setError('File path is required for metadata updates');
      return;
    }

    try {
      yjsDocumentService.updateMetadata(workspaceId, metaUpdate, filePath);
    } catch (err) {
      setError(err.message);
    }
  }, [workspaceId, filePath]);

  const disconnect = useCallback(() => {
    if (!workspaceId) return;
    try {
      yjsDocumentService.disconnectFromDocument(workspaceId);
      setIsConnected(false);
      setIsSynced(false);
    } catch (err) {
      setError(err.message);
    }
  }, [workspaceId]);

  return {
    content, metadata, updateContent, updateMetadata,
    isConnected, isSynced, connectedUsers, error,
    disconnect, workspaceId, filePath
  };
};

export default useYjsDocument;
